<template>
  <ModalComponent class="create-fields" v-bind="$attrs" ref="modal" v-on="$listeners">
    <Card variant="prompt" :title="$t(`${type}AddTitle`)" :subtitle="$t(`${type}AddSubtitle`)">
      <template>
        <Tags
          @tags-changed="$ev => addTag($ev)"
          class="create-fields__add"
          :label="$t('add')"
          :value="fields"
          :placeholder="$t('typeHere') + '...'"
          :autocomplete="`/api/residential/properties/${type}`"
          :validation="validation"
          :add-from-paste="false"
        >
          <template #after-label>
            <HelperPopover template="residential-properties-create" />
          </template>
        </Tags>

        <div class="create-fields__content">
          <div v-if="usedItems.length" class="create-fields__used-fields">
            <strong class="mb-1">{{ $t('residentialSettingsFieldsInUse') }}</strong>
            <div class="create-fields__used-fields-items">
              <ButtonComponent
                v-for="item in usedItems"
                :theme="getTheme(item)"
                :icon="`fa-pro-light:${getIcon(item)}`"
                @click="$ev => internalUpdate(item, item.preventDelete ? false : true, $ev)"
                :label="item.label"
                :disabled="Boolean(getDisabledField('properties.' + item.key))"
                v-tooltip.bottom="getDisabledField('properties.' + item.key)"
              >
              </ButtonComponent>
            </div>
          </div>
          <div class="create-fields__standard-fields">
            <strong class="mb-1">{{ $t('standardFields') }}</strong>
            <div class="create-fields__standard-fields-items">
              <ButtonComponent
                v-for="item in standardItems"
                :theme="getTheme(item)"
                icon="fa-pro-light:plus"
                :label="$t(item.key)"
                @click="internalUpdate(item)"
                :disabled="Boolean(getDisabledField('properties.' + item.key))"
                v-tooltip.bottom="getDisabledField('properties.' + item.key)"
              >
              </ButtonComponent>
            </div>
          </div>
          <div v-if="customItems.length" class="create-fields__custom-fields">
            <strong class="mb-1">{{ $t('customFields') }}</strong>
            <div class="create-fields__custom-fields-items">
              <ButtonComponent
                v-for="item in customItems"
                :theme="getTheme(item)"
                icon="fa-pro-light:plus"
                :label="item.label"
                @click="internalUpdate(item)"
              >
              </ButtonComponent>
            </div>
          </div>
        </div>
        <Alert v-if="removedItems.length" theme="warning">
          <span>{{
            $t('residentialsUpdateRemovePropertyWarning', {
              x: `"${removedItems.map(({ label }) => label).join(', ')}"`,
            })
          }}</span>
        </Alert>
      </template>

      <template #actions>
        <ButtonComponent :label="$t('cancel')" :icon="['fal', 'times']" @click="close" />
        <ButtonComponent
          v-if="internalItems.length"
          :label="addButtonLabel"
          theme="primary"
          :icon="['fas', 'arrow-right']"
          @click="updateItems"
          :promise="promise"
        />
      </template>
    </Card>
  </ModalComponent>
</template>

<script>
import Card from '@/components/Card-v2'
import { ModalComponent } from 'vue-elder-modal'
import Badge from '@/components/Badge'
import Tags from '@/components/Tags'
import { SortByKey } from '@/utils'
import { mapState } from 'vuex'
import HelperPopover from '@/components/HelperPopover/Component'
import { AlertComponent as Alert } from 'vue-elder-alert'

export default {
  props: {
    open: Boolean,
    title: [String, Number],
    resource: String,
    overrideTitle: String,
    items: {
      type: Array,
      default: () => [],
    },
    type: {
      default: 'property',
      enum: ['facility', 'property'],
    },
    custom: {
      type: Array,
      default: () => [],
    },
    used: {
      type: Array,
      default: () => [],
    },
    getDisabledField: {
      type: Function,
      default: () => false,
    },
  },
  data() {
    return {
      fields: [],
      internalItems: [],
      promise: null,
      removedItems: [],
    }
  },
  computed: {
    ...mapState({
      PropertiesEnum(state) {
        return state.enums.ResidentialProperties || []
      },
    }),
    addButtonLabel() {
      let base = `${this.$t('add')} (${
        this.internalItems.filter(i => !this.removedItems.find(r => r.key === i.key)).length
      })`
      if (this.removedItems.length) base = `${base}, ${this.$t('remove')} (${this.removedItems.length}) `
      return base
    },

    usedItems() {
      return [...this.used].map(i => ({
        ...i,
        inUse: (this.standardItems.find(({ key }) => i.key === key) || {}).inUse,
      }))
    },
    customItems() {
      const data = [...this.custom, ...this.internalItems.filter(i => i.key.startsWith('custom') || i.isCustom)]
      return data
    },
    allItems() {
      return [...this.internalItems, ...this.custom]
    },

    standardItems() {
      const standardInternal = this.internalItems.filter(i => !this.items.find(p => p.key === i.key && p.standard))
      return [...this.items, ...standardInternal]
        .filter(({ standard, inUse }) => Boolean(standard) || Boolean(inUse))
        .map(i => ({ ...i, label: i.label || this.$t(i.key) }))
        .sort(SortByKey('label'))
        .filter(({ key }) => !this.customItems.find(i => i.key === key))
    },

    validation() {
      return [
        {
          classes: 'tag-is-already-added',
          rule: item => {
            const isInternal = this.internalItems.find(i => i.key === item.key)
            const isUsed = [...this.customItems, ...this.items].find(i => i.key === item.key && i.inUse)
            return isInternal || isUsed || this.getDisabledField('properties.' + item.key)
          },
          disableAdd: true,
        },
      ]
    },
  },
  methods: {
    close() {
      if (!this.$refs.modal) return
      return this.$refs.modal.close()
    },
    addTag(tags) {
      if (!tags.length) return
      let tag = tags[0]

      const isAdded = this.allItems.find(i => i.label === tag.text)

      if (isAdded) tag = isAdded

      this.internalUpdate({
        label: tag.text,
        key: tag.key || `custom-${this.customItems.length}`,
        standard: Boolean(tag.key),
        isCustom: tag.isCustom,
      })

      this.fields = []
    },
    internalUpdate(item, remove = false, element) {
      const isAdded = this.internalItems.find(i => i.key === item.key)

      if (isAdded && remove && (this.$path('target.className', element) || '').includes('elder-button--icon')) {
        if (this.removedItems.find(i => i.key === item.key)) return
        return this.removedItems.push(item)
      } else if (remove) {
        let rIndex = this.removedItems.findIndex(i => i.key === item.key)
        if (~rIndex) this.removedItems.splice(rIndex, 1)
      }
      //prevent update used items
      if (item.inUse) return

      // for toggle remove from internal
      if (isAdded) {
        let index = this.internalItems.findIndex(i => i.key === item.key)
        if (!~index) return
        return this.internalItems.splice(index, 1)
      }
      //for internal adding
      this.internalItems.push(item)
    },

    getIcon(item) {
      if (this.removedItems.find(i => i.key === item.key)) return 'xmark'
      if (this.internalItems.find(i => i.key === item.key) && !item.preventDelete) return 'trash'
      return 'plus'
    },
    getTheme(item) {
      if (this.removedItems.find(i => i.key === item.key)) return 'error'
      if (this.internalItems.find(i => i.key === item.key)) return 'success'
      return item.inUse ? 'secondary' : 'default'
    },

    updateItems() {
      this.promise = Promise.all(
        this.internalItems.map(item => {
          return new Promise(resolve =>
            setTimeout(() => {
              resolve(
                this.$emit('add', {
                  ...item,
                  label: [...this.customItems, ...this.usedItems].find(({ key }) => key === item.key) ? item.label : '',
                  value: this.removedItems.find(({ key }) => key === item.key) ? 'null' : item.value,
                }),
              )
            }, 100),
          )
        }),
      ).then(() => {
        this.close()
        this.internalItems = []
        this.removedItems = []
      })
    },
  },

  components: {
    ModalComponent,
    Card,
    Badge,
    Tags,
    HelperPopover,
    Alert,
  },
}
</script>

<style lang="scss">
.create-fields {
  .card-v2 {
    max-width: 900px;
    overflow: visible;
    &__content {
      display: flex;
      gap: 2.5rem;
      flex-direction: column;
    }
  }

  &__content {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
    justify-content: space-between;
    gap: 2rem;

    & > *:nth-child(2) {
      padding-left: 2rem;
      border-left: 1px solid var(--border-color);
    }
  }

  &__used-fields-items,
  &__custom-fields-items,
  &__standard-fields-items {
    margin-top: 1rem;
    display: flex;
    flex-flow: wrap;
    gap: 0.5rem;

    .elder-button {
      flex-grow: 0;
      width: fit-content;
      justify-content: space-between;
      border-radius: 3rem;
      font-size: 0.9em;
      gap: 0.5em;
      --vue-elder-button-padding-y: 4px;
      --vue-elder-button-padding-x: 10px;
      &:focus:before {
        opacity: 0 !important;
      }

      &--secondary {
        cursor: not-allowed;
      }
      &--success {
        background-color: var(--light-green) !important;
        color: var(--dark-green) !important;
      }
      &--error {
        background-color: hsl(var(--error-h), var(--error-s), 95%) !important;
        color: var(--error) !important;
      }
    }
  }

  &__used-fields-items {
    .elder-button__label {
      border-right: 1px solid currentColor;
    }
    .elder-button--default {
      .elder-button__label {
        border-color: var(--light-grey);
      }
    }
  }
  &__standard-fields {
    &-items {
      display: flex;
      flex-flow: row wrap;
      gap: 0.5rem;
    }

    // grid-template-rows: repeat(2, 1fr);
  }
  &__add {
    grid-column-end: span 2;
  }
  .field-tags .vue-tags-input .ti-autocomplete {
    box-shadow: none;
  }
  .tag-is-already-added {
    opacity: 0.6;
    text-decoration: line-through;
  }
}
</style>
